import {
  ConfigProvider,
  Divider,
  Select,
  message,
  Modal,
  Input,
  Switch,
} from "antd";
import React, { useEffect, useState } from "react";
import { validateUserEditDataForSuperAdmin } from "../../services/user-service";
import useUserStore from "../../stores/store";
import ApiService from "../../utils/ApiService";

const roleList = [
  {
    value: "Admin",
    label: "Admin",
    branches: [
      { value: "Admin", label: "Admin" },
      { value: "Staff", label: "Staff" },
      { value: "Chef", label: "Chef" },
    ],
  },
  { value: "Staff", label: "Staff" },
  { value: "Chef", label: "Chef" },
];

function EditUserModal({ openModal, params }) {
  const { user } = useUserStore();

  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [parentRestaurantList, setParentRestaurantList] = useState([]);
  const [restaurantList, setRestaurantList] = useState([]);
  const [parentRestaurantId, setParentRestaurantId] = useState("");
  const [restaurantId, setRestaurantId] = useState("");
  const [currentBranchId, setCurrentBranchId] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState(true);

  const clearFields = () => {
    setName("");
    setParentRestaurantId("");
    setRestaurantId("");
    setRole("");
    setStatus(true);
    setError("");
  };

  // Initialize form values when modal opens or selected item changes
  useEffect(() => {
    if (params?.selectedItem) {
      setName(params.selectedItem.name || "");
      setParentRestaurantId(params.selectedItem.parentRestaurantInfo?.id || "");
      setRestaurantId(params.selectedItem.branchInfo?.id || "");
      setCurrentBranchId(params.selectedItem.branchInfo?.id || ""); // Add this line
      setRole(params.selectedItem.role || "");
      setStatus(params.selectedItem.status === "On");
    }
  }, [params]);

  // Set parent restaurant list when it changes in params
  useEffect(() => {
    if (params?.allRestaurantList) {
      setParentRestaurantList(params.allRestaurantList);
    }
  }, [params?.allRestaurantList]);

  // Set restaurant list for the selected parent restaurant
  useEffect(() => {
    if (parentRestaurantId) {
      const parentItem = parentRestaurantList.find(
        (item) => item.value === parentRestaurantId
      );
      setRestaurantList(parentItem?.branches || []);
    }
  }, [parentRestaurantId, parentRestaurantList]);

  // Reset form when modal closes
  useEffect(() => {
    if (!openModal) {
      setName("");
      setParentRestaurantId("");
      setRestaurantId("");
      setRole("");
      setStatus(true);
      setError("");
    }
  }, [openModal]);

  const onSelectParentRestaurant = (val) => {
    const parentItem = parentRestaurantList.find((item) => item.value === val);
    setRestaurantId(""); // Reset branch when parent restaurant changes
    setRestaurantList(parentItem?.branches || []);
    setParentRestaurantId(val);
  };

  const handleOk = async () => {
    // First validate the required fields
    const errors = validateUserEditDataForSuperAdmin({
      restaurantId, // Keep existing validation
      role,
    });

    if (errors.length > 0) {
      message.error(errors[0]);
      return;
    }

    // Construct the payload object with all necessary data
    const payload = {
      id: params?.selectedItem?.id,
      name: name.trim(),
      role: role,
      parentRestaurantId: parentRestaurantId,
      branchId: restaurantId,
      currentBranchId: currentBranchId, // Add this line
      status: status ? "On" : "Off",
    };
    
    try {
      // Rest of the code remains unchanged
      const response = await ApiService.EDIT_RESTAURANT_USER(
        payload,
        user.token.token
      );

      if (response.code === 200) {
        message.success("User updated successfully");
        // Notify the parent component and close the modal
        clearFields();
        params.onFinish({ showModal: false });
      } else {
        throw new Error(response.message || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      message.error(
        error.message ||
          "An error occurred while updating user. Please try again."
      );
    }
  };

  const handleCancel = () => {
    clearFields();
    params.onCancel();
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: "#FFD700",
            defaultHoverColor: "#000000",
            fontFamily: "ubold",
          },
        },
      }}
    >
      <Modal
        open={openModal}
        okButtonProps={{
          style: {
            backgroundColor: "#FFD700",
            color: "#000000",
            fontFamily: "ubold",
          },
        }}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <div className="flex flex-col font-uregular">
          <div className="text-2xl font-ubold mb-6">Edit User</div>

          {/* User Info Section */}
          <label className="text-[15px] font-bold mb-2">User Info</label>

          {/* Editable Name Field */}
          <div className="flex flex-col mb-3">
            <div className="text-sm font-uregular font-bold flex flex-row">
              <div>Name</div>
              <div className="font-ubold text-red-700 ml-[4px]">*</div>
            </div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter user name"
              className="mt-1"
            />
          </div>

          {/* Non-editable Phone Number */}
          <div className="flex flex-col mb-1">
            <div className="text-sm font-uregular font-bold">Phone Number</div>
            <Input
              value={params?.selectedItem?.phone || "N/A"}
              disabled
              className="mt-1 bg-gray-50"
            />
          </div>

          {/* Status Toggle */}
          <div className="flex flex-col mt-3">
            <div className="text-sm font-uregular font-bold flex flex-row items-center">
              <div>Status</div>
              <Switch
                checked={status}
                onChange={setStatus}
                className="ml-2"
                checkedChildren={<span style={{ color: "#000000" }}>On</span>} // Black text for "On"
                unCheckedChildren={
                  <span style={{ color: "#000000" }}>Off</span>
                } // Black text for "Off"
                style={{
                  backgroundColor: status ? "#FFD700" : undefined, // Yellow when "On"
                  borderColor: "#FFD700",
                }}
              />
            </div>
          </div>

          <Divider />

          {/* Parent Restaurant List */}
          <div className="flex flex-col mb-[12px]">
            <div className="text-sm font-uregular font-bold flex flex-row">
              <div>Restaurant List</div>
              <div className="font-ubold text-red-700 ml-[4px]">*</div>
            </div>
            {parentRestaurantList.length > 0 && (
              <Select
                className="font-uregular mt-1"
                showSearch
                value={parentRestaurantId}
                placeholder="Select restaurant name"
                onChange={onSelectParentRestaurant}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={parentRestaurantList}
              />
            )}
          </div>

          {/* Branch List */}
          <div className="flex flex-col mb-[12px]">
            <div className="text-sm font-uregular font-bold flex flex-row">
              <div>Branch List</div>
              <div className="font-ubold text-red-700 ml-[4px]">*</div>
            </div>
            <Select
              className="font-uregular mt-1"
              value={restaurantId}
              placeholder="Select branch name"
              onChange={(value) => setRestaurantId(value)}
              options={restaurantList}
            />
          </div>

          {/* Role */}
          <div className="flex flex-col mb-[12px]">
            <div className="text-sm font-uregular font-bold flex flex-row">
              <div>Role</div>
              <div className="font-ubold text-red-700 ml-[4px]">*</div>
            </div>
            <Select
              className="font-uregular mt-1"
              value={role}
              placeholder="Select role"
              onChange={(value) => setRole(value)}
              options={roleList}
            />
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
}

export default EditUserModal;
